import type { UtilityPatch } from './adminQueries';
import { normalizeAmiStatus } from './utilityRoutes';
import { isUsableTemplate } from './requestForm';
import type { AmiStatus, UtilityRow } from '../types/utility';

/**
 * The Admin Utilities editor's draft: every editable column as the string an
 * input holds, and the rules that turn a draft back into the columns that
 * changed. Kept out of the component so the rules are unit-tested and the
 * component file exports only a component.
 */

/** A draft of one utility's editable fields, as strings the inputs can hold. */
export interface Draft {
  name: string;
  state: string;
  ami_status: AmiStatus;
  ami_contact_email: string;
  request_form_template: string;
  portal_url: string;
  download_steps: string;
  export_format: string;
  history_months: string;
  rollout_note: string;
  instructions_source_url: string;
  instructions_verified: boolean;
  ami_notes: string;
  notes: string;
  is_active: boolean;
}

export function draftOf(u: UtilityRow): Draft {
  return {
    name: u.name,
    state: u.state,
    ami_status: normalizeAmiStatus(u.ami_status),
    ami_contact_email: u.ami_contact_email ?? '',
    request_form_template: u.request_form_template ?? '',
    portal_url: u.portal_url ?? '',
    download_steps: u.download_steps ?? '',
    export_format: u.export_format ?? '',
    history_months: u.history_months == null ? '' : String(u.history_months),
    rollout_note: u.rollout_note ?? '',
    instructions_source_url: u.instructions_source_url ?? '',
    instructions_verified: u.instructions_verified === true,
    ami_notes: u.ami_notes ?? '',
    notes: u.notes ?? '',
    is_active: u.is_active !== false,
  };
}

function nullable(value: string): string | null {
  const t = value.trim();
  return t.length > 0 ? t : null;
}

/** Turn a draft back into the columns that changed. */
export function patchFromDraft(original: UtilityRow, d: Draft): { patch: UtilityPatch; error: string | null } {
  const name = d.name.trim();
  const state = d.state.trim().toUpperCase();
  if (!name) return { patch: {}, error: 'The utility needs a name.' };
  if (!/^[A-Z]{2}$/.test(state)) return { patch: {}, error: 'State must be the two-letter code.' };
  let months: number | null = null;
  if (d.history_months.trim() !== '') {
    const n = Number.parseInt(d.history_months, 10);
    if (!Number.isFinite(n) || n <= 0) return { patch: {}, error: 'Months of history must be a whole number, or blank.' };
    months = n;
  }
  const email = d.ami_contact_email.trim();
  if (d.ami_status === 'email_request' && !email) {
    return { patch: {}, error: 'An email-request utility needs a contact email, or the route cannot be offered.' };
  }
  const template = d.request_form_template.trim();
  if (template && !isUsableTemplate(template)) {
    return { patch: {}, error: 'The request form link must be a full https:// address.' };
  }
  if (d.ami_status === 'customer_request' && !email && !template) {
    return { patch: {}, error: 'A customer-request utility needs a request form link or the address customers write to, or the route cannot be offered.' };
  }

  const next: UtilityPatch = {
    name,
    state,
    ami_status: d.ami_status,
    ami_contact_email: email,
    request_form_template: template || null,
    portal_url: nullable(d.portal_url),
    download_steps: nullable(d.download_steps),
    export_format: nullable(d.export_format),
    history_months: months,
    rollout_note: nullable(d.rollout_note),
    instructions_source_url: nullable(d.instructions_source_url),
    instructions_verified: d.instructions_verified,
    ami_notes: nullable(d.ami_notes),
    notes: nullable(d.notes),
    is_active: d.is_active,
  };
  // A hand-set verified flag records when; clearing it clears the stamp. The
  // homeowner path sets both itself.
  if (d.instructions_verified !== (original.instructions_verified === true)) {
    next.instructions_verified_at = d.instructions_verified ? new Date().toISOString() : null;
  }

  const patch: UtilityPatch = {};
  for (const key of Object.keys(next) as (keyof UtilityPatch)[]) {
    const before = key === 'ami_status' ? normalizeAmiStatus(original.ami_status) : (original[key] ?? null);
    const after = next[key] ?? null;
    if (before !== after) (patch as Record<string, unknown>)[key] = after;
  }
  return { patch, error: null };
}

